import { createPassportAuth } from '../../../Helpers/passportAuth'
import type { FetchOptions, FetchRequest } from 'ofetch'
import { ElNotification } from 'element-plus'

export default defineNuxtPlugin((nuxtApp) => {
    const apiBase = String(nuxtApp.$config.public.apiBase)
    const useDevProxy = import.meta.dev && import.meta.client && /^https?:\/\//.test(apiBase)
    const passportAuth = createPassportAuth('storefront', apiBase, {
        restoreOnlyAfterLogin: true,
        proxyOrigin: useDevProxy ? window.location.origin : undefined,
        language: () => String(nuxtApp.$i18n.locale.value || nuxtApp.$i18n.defaultLocale),
    })
    passportAuth.onLogout(() => useAuthStore().setToken(null))
    passportAuth.onSessionExpired(() => {
        ElNotification.warning({ message: nuxtApp.$i18n.t('auth_session_expired') })
        void nuxtApp.runWithContext(() => navigateTo('/login'))
    })
    if (import.meta.client) {
        // Retire the JavaScript-readable cookie from the previous authentication system.
        document.cookie = 'access_token=; Max-Age=0; Path=/; SameSite=Lax'
    }
    const raw = $fetch.create({
        baseURL: useDevProxy ? new URL(apiBase).pathname : apiBase,
        retry: 0,
        async onRequest({ options }) {
            const headers = new Headers(options.headers)
            const token = await passportAuth.accessToken()
            headers.set('Accept-Language', String(nuxtApp.$i18n.locale.value || nuxtApp.$i18n.defaultLocale))
            headers.set('Accept', 'application/json')
            if (token) headers.set('Authorization', `Bearer ${token}`)
            else headers.delete('Authorization')
            options.headers = headers
        },
        onResponse({ response }) {
            if (response?._data && typeof response._data === 'object' && 'data' in response._data) response._data = response._data.data
        },
    })
    const api = async <T = unknown>(request: FetchRequest, options: FetchOptions<'json'> = {}): Promise<T> => {
        const token = await passportAuth.accessToken()
        try {
            return await raw<T>(request, options as any) as T
        } catch (error: any) {
            if (import.meta.client && error.response?.status === 401 && token) {
                const renewed = await passportAuth.refresh(token)
                if (renewed) return await raw<T>(request, options as any) as T
                await nuxtApp.runWithContext(() => navigateTo('/login'))
            }
            throw error
        }
    }
    return { provide: { api, passportAuth } }
})
